import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Search, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";
import api from "../lib/api";
import { format } from "date-fns";

/**
 * Reusable enterprise data table page.
 *
 * props:
 *  - title, description
 *  - endpoint (e.g. "/audits")
 *  - columns: [{ key, label, render?, sortable? }]
 *  - filters: [{ key, label, options: [{label,value}] }]
 *  - searchKeys: string[] for client-side search
 *  - pageSize
 *  - initialSort: { key, dir }
 */
export default function DataTablePage({
  title,
  description,
  endpoint,
  columns,
  filters = [],
  searchKeys = [],
  pageSize = 10,
  initialSort,
  actions,
  testIdPrefix,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterVals, setFilterVals] = useState({});
  const [sort, setSort] = useState(initialSort || { key: null, dir: "asc" });
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(endpoint);
        setItems(data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [endpoint]);

  const filtered = useMemo(() => {
    let out = items;
    if (q) {
      const s = q.toLowerCase();
      out = out.filter((it) => searchKeys.some((k) => (it[k] || "").toString().toLowerCase().includes(s)));
    }
    Object.entries(filterVals).forEach(([k, v]) => {
      if (v) out = out.filter((it) => it[k] === v);
    });
    if (sort.key) {
      out = [...out].sort((a, b) => {
        const av = a[sort.key];
        const bv = b[sort.key];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === "number" && typeof bv === "number") {
          return sort.dir === "asc" ? av - bv : bv - av;
        }
        return sort.dir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      });
    }
    return out;
  }, [items, q, filterVals, sort, searchKeys]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [q, filterVals]);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
        </div>
        {actions}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid={`${testIdPrefix}-search`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
                placeholder="Search…"
              />
            </div>
            {filters.map((f) => (
              <select
                key={f.key}
                data-testid={`${testIdPrefix}-filter-${f.key}`}
                value={filterVals[f.key] || ""}
                onChange={(e) => setFilterVals({ ...filterVals, [f.key]: e.target.value })}
                className="h-10 w-full sm:w-44 rounded-md border border-border bg-card px-3 text-sm"
              >
                <option value="">{f.label}</option>
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/60 backdrop-blur">
                <tr className="border-t border-b border-border">
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      className={`text-left py-2.5 px-6 label-eyebrow ${c.sortable !== false ? "cursor-pointer select-none" : ""}`}
                      onClick={() => c.sortable !== false && toggleSort(c.key)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {c.sortable !== false && <ArrowUpDown className="w-3 h-3 opacity-50" />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={columns.length} className="py-8 text-center text-muted-foreground">Loading…</td></tr>
                )}
                {!loading && pageItems.length === 0 && (
                  <tr><td colSpan={columns.length} className="py-8 text-center text-muted-foreground">No results.</td></tr>
                )}
                {pageItems.map((row) => (
                  <tr
                    key={row.id}
                    data-testid={`${testIdPrefix}-row-${row.id}`}
                    className="border-b border-border hover:bg-muted/40 transition-colors"
                  >
                    {columns.map((c) => (
                      <td key={c.key} className="py-3 px-6 align-middle">
                        {c.render ? c.render(row) : row[c.key] ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-border text-sm text-muted-foreground">
            <span>
              Showing {filtered.length === 0 ? 0 : (page - 1) * pageSize + 1}–
              {Math.min(page * pageSize, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                data-testid={`${testIdPrefix}-prev`}
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <span className="px-2 text-xs">
                Page {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                data-testid={`${testIdPrefix}-next`}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export const formatDate = (d) => (d ? format(new Date(d), "MMM d, yyyy") : "—");
export function StatusBadge({ value, map }) {
  const c = map[value] || { v: "neutral", label: value };
  return <Badge variant={c.v}>{c.label}</Badge>;
}
